import { PROPOSAL_SUBSCRIPTION_TOPIC } from 'constants/index';
import _ from 'lodash';

import helpers from 'helpers';

let time = 0;
let isOn = false;
let start = 0;
let timer;
let sessionSaved = false;
let clientIP = '';
const MAX_SESSION_TIME = 5 * 60 * 1000;
let isSavingForm = false;

// pause the clocks after this much time without any interaction, so that a
// proposal left open on screen stops counting towards `watched`
const IDLE_TIMEOUT = 60 * 1000;
const IDLE_CHECK_INTERVAL = 5 * 1000;
let lastActivityAt = 0;
let idleWatcher;

// refs owned by Publish.js, registered once so that the visibility/idle
// handlers can pause the per-page clock alongside the main one
let pageRefs = null;

const onSavingFormChange = (_isSavingForm) => {
  const wasSavingForm = isSavingForm;
  isSavingForm = _isSavingForm;

  if (wasSavingForm === _isSavingForm) {
    return;
  }

  // logTimer already refuses to save mid-form-save; stop the clocks too so the
  // save window isn't silently attributed to whichever page is on screen
  if (_isSavingForm) {
    pauseAll();
  } else {
    resumeAll();
  }
};

const registerPageRefs = (refs) => {
  pageRefs = refs;
};

/**
 * Bank the time spent on the current page into pageTimerRef, as of `asOf`.
 * Leaves the page clock running (re-stamped to `asOf`).
 */
const flushPageTime = (refs = pageRefs, asOf = Date.now()) => {
  const { pageTimerRef, currentPageRef, pageStartTimeRef } = refs || {};

  if (!pageTimerRef?.current || currentPageRef?.current == null) {
    return;
  }

  // page clock is paused (never started, or stopped while hidden/idle)
  if (pageStartTimeRef?.current == null) {
    return;
  }

  const currentPage = currentPageRef.current;
  const elapsedSeconds = Math.max(0, asOf - pageStartTimeRef.current) / 1000;

  pageTimerRef.current = {
    ...pageTimerRef.current,
    [currentPage]: (pageTimerRef.current[currentPage] || 0) + elapsedSeconds,
  };
  pageStartTimeRef.current = asOf;
};

const pausePageClock = (asOf = Date.now()) => {
  flushPageTime(pageRefs, asOf);

  if (pageRefs?.pageStartTimeRef) {
    pageRefs.pageStartTimeRef.current = null;
  }
};

const resumePageClock = () => {
  if (pageRefs?.pageStartTimeRef && pageRefs.pageStartTimeRef.current == null) {
    pageRefs.pageStartTimeRef.current = Date.now();
  }
};

const startTimer = (logTime) => {
  if (!isOn) {
    isOn = true;
    time = logTime;
    start = Date.now() - logTime;
    lastActivityAt = Date.now();
    timer = setInterval(() => {
      time = Date.now() - start;
    }, 1000);
  }
};

const startTimerMobile = (logTime) => {
  startTimer(logTime);
};

/**
 * Stop the main clock, keeping the time accrued so far (as of `asOf`).
 * Unlike resetTimer this does not discard `time` — resumeTimer picks it back up.
 */
const pauseTimer = (asOf = Date.now()) => {
  if (!isOn) {
    return;
  }

  time = Math.max(0, asOf - start);
  clearInterval(timer);
  isOn = false;
};

const resumeTimer = () => {
  if (isOn) {
    return;
  }

  isOn = true;
  start = Date.now() - time;
  timer = setInterval(() => {
    time = Date.now() - start;
  }, 1000);
};

/** Pause both clocks together — used on tab hide / blur / idle. */
const pauseAll = (asOf = Date.now()) => {
  pauseTimer(asOf);
  pausePageClock(asOf);
};

/** Resume both clocks together — used on tab show / focus / interaction. */
const resumeAll = () => {
  lastActivityAt = Date.now();
  resumeTimer();
  resumePageClock();
};

/**
 * Record user interaction. Resumes the clocks if they were paused for idle,
 * but never while the page is hidden — that stays the visibility handler's call.
 */
const markActivity = () => {
  lastActivityAt = Date.now();

  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
    return;
  }

  resumeTimer();
  resumePageClock();
};

const startIdleWatcher = () => {
  if (idleWatcher) {
    return;
  }

  lastActivityAt = Date.now();
  idleWatcher = setInterval(() => {
    if (isOn && Date.now() - lastActivityAt >= IDLE_TIMEOUT) {
      // bank only up to the last interaction, not the idle stretch itself
      pauseAll(lastActivityAt);
    }
  }, IDLE_CHECK_INTERVAL);
};

const stopIdleWatcher = () => {
  clearInterval(idleWatcher);
  idleWatcher = null;
};

const resetTimer = (setLogTime) => {
  clearInterval(timer);
  setLogTime(0);
  time = 0;
  isOn = false;
  start = 0;

  // a save can land while the reader is still on the page (e.g. they came back
  // before the mutation resolved) — start counting again from zero instead of
  // waiting for the next interaction to notice
  const isVisible = typeof document === 'undefined' || document.visibilityState === 'visible';

  if (isVisible && !isSavingForm && Date.now() - lastActivityAt < IDLE_TIMEOUT) {
    resumeTimer();
    resumePageClock();
  }
};

const logTimer = async ({
  isClient,
  prop,
  updateProposalClient,
  trackProposalTime,
  setLogTime,
  session,
  pageTimerRef,
  currentPageRef,
  pageStartTimeRef,
}) => {
  // prevent updating when saving is going on
  if (!prop || !isClient || isSavingForm) {
    return;
  }

  // prevent updating when prerendering (for slack or whatsapp)
  const userAgent = (navigator?.userAgent || '').toLowerCase();
  if (userAgent.includes('prerender')) {
    return;
  }

  if (clientIP === '') {
    // fetch client ip only if not fetched before
    try {
      let response = await fetch('https://www.cloudflare.com/cdn-cgi/trace');
      if (response) {
        response = await response.text();
        const result = helpers.transformReadableText(response);
        if (result?.ip) {
          clientIP = result.ip;
        }
      }
    } catch (error) {
      console.error(error);
    }
  }

  let openLogs = prop?.openLogs?.slice() || [];
  const utcTime = new Date(new Date().toUTCString()).toISOString();

  const browserName = await helpers.getBrowserName();

  if (!clientIP || clientIP === '') {
    clientIP = browserName;
  }

  const identifier = `${clientIP}-${session.tab_id}`;

  const logIndex = openLogs.findIndex((log) => identifier === log.clientIP);

  // Flush current page time into pageTimerRef (covers single-page proposals where user never scrolls)
  flushPageTime({ pageTimerRef, currentPageRef, pageStartTimeRef });

  // page time accrued since the last successful save; like `watched` this is an
  // increment, and is cleared once the mutation lands
  const pageTime = pageTimerRef?.current ?? {};

  let newSession = false;
  // if session is not saved AND
  // visit=1 OR last visit time was > 5 min ago then new session

  if (
    !sessionSaved &&
    (session?.visit === 1 || Date.now() - session?.last_visited > MAX_SESSION_TIME)
  ) {
    newSession = true;
  }

  if (newSession || logIndex === -1) {
    // new session
    openLogs = [
      {
        date: utcTime,
        clientIP: identifier,
        watched: Math.max(Number(time) / 1000, 2), // at least 2 seconds
        pageTime,
        deviceInfo: {
          deviceType: /Mobi|Android/i.test(navigator.userAgent) ? 'Mobile' : 'Desktop',
          deviceOS: helpers.getOS(),
          deviceBrowser: browserName,
          deviceName: helpers.getDeviceName(),
          deviceAgent: window.navigator.userAgent,
        },
      },
      ...openLogs,
    ];
  } else {
    // update old session — merge page times into what was already saved rather
    // than replacing them, otherwise earlier pages of this session are lost
    const mergedPageTime = { ...(openLogs[logIndex].pageTime || {}) };

    Object.keys(pageTime).forEach((page) => {
      mergedPageTime[page] = Number(mergedPageTime[page] || 0) + Number(pageTime[page] || 0);
    });

    openLogs[logIndex] = {
      ...openLogs[logIndex],
      watched: Number(openLogs[logIndex].watched) + Number(time) / 1000,
      pageTime: mergedPageTime,
    };
  }

  sessionSaved = true;

  const onSaved = ({ data }) => {
    if (data) {
      resetTimer(setLogTime);

      // page time is saved as an increment, so clear the accumulator once it
      // has landed — otherwise the next save counts the same seconds again
      if (pageTimerRef?.current != null) {
        pageTimerRef.current = {};
      }
    }
  };

  // Send only this session's entry; the whole array overwrote sessions other viewers
  // added meanwhile and cost the server a full proposal read and write
  if (trackProposalTime) {
    const startsSession = newSession || logIndex === -1;
    trackProposalTime({
      variables: {
        proposalId: prop._id,
        session: startsSession ? openLogs[0] : openLogs[logIndex],
        newSession: startsSession,
      },
    }).then(onSaved);
    return;
  }

  updateProposalClient({
    variables: {
      topic: `${PROPOSAL_SUBSCRIPTION_TOPIC}_${prop?.channel || prop?.auid || prop?.uid}`,
      updateType: 'time',
      proposal: {
        id: prop._id,
        _id: prop._id,
        pid: prop.pid,
        uid: prop.uid,
        openLogs: openLogs,
        lastSeen: utcTime,
      },
    },
  }).then(onSaved);
};

const setseen = _.once((params, prop, updateProposalClient, clientWixPreview) => {
  const userAgent = (navigator?.userAgent || '').toLowerCase();
  if (prop?.state === 'seen' || prop?.state === 'approved' || userAgent.includes('prerender')) {
    return;
  }

  const isClient = !!params.cid || !!clientWixPreview;

  if ((params.cid || clientWixPreview) && isClient && prop) {
    updateProposalClient({
      variables: {
        topic: `${PROPOSAL_SUBSCRIPTION_TOPIC}_${prop?.channel || prop?.auid || prop?.uid}`,
        updateType: 'open',
        proposal: {
          id: prop._id,
          _id: prop._id,
          pid: prop.pid,
          uid: prop.uid,
          client: prop.client,
          state: 'seen',
        },
      },
    }).then(({ data }) => {
      if (data) {
        try {
          var thedate = new Date();
          if (
            window.location.href.indexOf('/cd/') === -1 &&
            window.location.href.indexOf('/proposal-preview') === -1
          ) {
            window.ga('set', 'dimension2', thedate.getMonth() + 1 + '-' + thedate.getFullYear());
            window.ga('send', 'event', 'prop-state', 'seen', window.location.href);
          }
        } catch (error) {
          console.log(error);
        }
      } else {
        return true;
      }
    });
  }
});

const getTime = () => time;

const proposalTimer = {
  startTimer,
  startTimerMobile,
  getTime,
  // resetTimer,
  logTimer,
  // setSeenCount,
  setseen,
  onSavingFormChange,
  registerPageRefs,
  pauseAll,
  resumeAll,
  markActivity,
  startIdleWatcher,
  stopIdleWatcher,
};

export default proposalTimer;
